import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { authService } from "../services/auth";
import { useAuthStore } from "../store/authStore";
import { CaptchaWidget } from "../components/CaptchaWidget";
import type { LoginInput } from "@clawplete/shared/types";

// Test mode bypass for E2E testing - checks env var OR URL param
const isEnvTestMode = import.meta.env.VITE_TEST_MODE === "true";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [formData, setFormData] = useState<LoginInput>({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState(false);

  // Check for test mode via URL param or env var - set token on mount
  const testMode = isEnvTestMode || searchParams.get("test") === "true";

  useEffect(() => {
    if (testMode) {
      setCaptchaToken("test-bypass");
    }
  }, [testMode]);

  const loginMutation = useMutation({
    mutationFn: ({ formData, captchaToken }: { formData: LoginInput; captchaToken: string }) =>
      authService.login(formData, captchaToken),
    onSuccess: (data) => {
      setAuth(data.user, data.tokens);
      navigate("/dashboard");
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || "Login failed");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Trim whitespace from email and password to handle copy-paste issues
    const trimmedData = {
      email: formData.email.trim(),
      password: formData.password.trim(),
    };

    if (!captchaToken) {
      setCaptchaError(true);
      setError("Please complete the CAPTCHA to continue");
      return;
    }

    loginMutation.mutate({ formData: trimmedData, captchaToken });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-900 px-4">
      <div className="max-w-md w-full card">
        <div className="text-center mb-8">
          <span className="text-4xl">🦞</span>
          <h1 className="text-3xl font-bold text-primary-400 mt-4">
            Welcome to Clawplete
          </h1>
          <p className="text-gray-400 mt-2">Sign in to your account</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          {/* Cloudflare Turnstile CAPTCHA - skip in test mode */}
          {!testMode && (
            <div>
              <CaptchaWidget
                onVerify={(token) => {
                  setCaptchaToken(token);
                  setCaptchaError(false);
                }}
                onError={() => {
                  setCaptchaToken(null);
                  setCaptchaError(true);
                  setError("CAPTCHA validation failed. Please try again.");
                }}
                onExpire={() => {
                  setCaptchaToken(null);
                }}
                theme="auto"
              />
            </div>
          )}
          {testMode && captchaToken && (
            <div className="text-green-500 text-sm flex items-center gap-2">
              <span>✓</span> Complete the CAPTCHA to continue
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? "Logging in..." : "Login"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          Don't have an account?{" "}
          <Link to="/register" className="text-primary-400 hover:text-primary-300 font-medium">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}
